import {
	ComponentType,
	ReactElement,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { Activity, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { Model } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import {
	OverviewActivity,
	OverviewEngineFactories
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import { JSONDocument } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/models";
import {
	ExpressionColumn,
	OverviewModel,
	ReferenceColumn
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/overview-model";
import { Footer } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/footer";
import { OverviewButton } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/overview-button";
import { TableBodyCell } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/table-body-cell";
import {
	ComponentMap,
	DefaultComponentMap
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import {
	DefaultWidgetMap,
	WidgetMap
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/widget-map";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { ContentBoxProps } from "@com.mgmtp.a12.widgets/widgets-core/lib/contentbox";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";
import { Link } from "@com.mgmtp.a12.widgets/widgets-core/lib/link";
import { RowStyleGetter } from "@com.mgmtp.a12.widgets/widgets-core/lib/table/new-api";

import { getFVManifest } from "../../../app/slices/fachverfahren/selectors";
import { AssigneeCandidate } from "../../../app/slices/users/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { DocumentModelContextProvider } from "../../../component/auxiliaryOverviewEngine/context";
import { ClientModal, ClientModalProps } from "../../../component/ClientModal";
import { CustomFormatReferenceCell } from "../../../component/CustomFormatReferenceCell";
import { CustomOverviewButton } from "../../../component/CustomOverviewButton";
import { CustomPopUpMenu } from "../../../component/CustomPopUpMenu";
import { ExternalEnumOverviewEngine } from "../../../component/externalEnumOverviewEngine";
import { ScopeBasedVisibilityDecorator } from "../../../component/ScopeBasedVisibilityDecorator";
import { OverviewFooterWithCount } from "../../../component/OverviewFooterWithCount";
import { assertNonNullable } from "../../../utilities/assert";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { isAdmin, isUser, selectHasNeededPermissions, selectStructuredRoles } from "../../../utilities/user";
import { EMAIL_KONTAKLISTE_APPIDS } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { assignProcessDocument } from "../../assignment/actions";
import { OverviewAssigneePicker } from "../../assignment/components/AssigneePicker";
import { selectAssigneeCandidatesCreator } from "../../assignment/selectors";
import { getViewHtmlId } from "../../focusHandling/sagas";
import { GeneralOverviewWidgetsMap } from "../../generalOverviewEngine/GeneralOverviewEngine";
import {
	isDocumentWithProcessMetadata,
	isProcessOverviewData,
	LinkedProcessInfo,
	ProcessMetadataContainingDocument
} from "../../workflows/data/processMetadata";
import { extractConfigList } from "../../../utilities/platzhalterHelper";
import { setLastCreatedProcessBusinessKey } from "../../../app/slices/operation/reducers";

import {
	exportApplicationData,
	openInvitationModal,
	openTaskDocument,
	openTaskDocumentAndShowWiedervorlagen
} from "../store/actions";

import { createNewAntrag } from "./common";
import { CustomContextMenu } from "./customContextMenu";
import {
	CustomEnumerationFilterOptionsView,
	CustomFilter,
	CustomFilterBar,
	CustomFilterButton,
	CustomFilterSelector,
	CustomFilterSelectorMobile,
	CustomFilterSelectorWidget,
	CustomStringFilterOptionsView
} from "./customFilter";

const logger = LoggerFactory.getLogger("ProcessOverviewEngine");

const MODELS_WITHOUT_PAGINATION = ["Recent_Changes_O"];

const StyledTableBody = styled(DefaultComponentMap.TableBody)`
	* [data-role="table-body-cell"] {
		justify-content: center;
	}
`;

export function ProcessOverviewEngine(props: OverviewEngineFactories.ViewComponentProps): ReactNode {
	const { activityId, name, modelDescriptors } = props;
	const [documents, setDocuments] = useState<ProcessMetadataContainingDocument[]>([]);

	const data = useSelector(ActivitySelectors.data(activityId));

	const user = useSelector(UaaSelectors.user);
	const hasOidcUser = isUser(user);

	const fachverfahren = useSelector(
		ActivitySelectors.activityPropById(activityId, it => it.descriptor.fachverfahren)
	);
	const fachverfahrenManifest = useSelector(getFVManifest(fachverfahren));

	const dispatch = useDispatch();

	useEffect(() => {
		if (!isProcessOverviewData(data)) {
			return;
		}
		setDocuments(data.documents);
		dispatch(setLastCreatedProcessBusinessKey({ lastCreatedProcessBusinessKey: "" }));
	}, [data]);

	const rowStyling = useCallback<RowStyleGetter<JSONDocument>>(
		({ rowIndex }) => {
			if (!OverviewActivity.Data.DocumentListData.isInstance(data)) {
				return {};
			}

			const processDocument = data?.documents[rowIndex];

			return {
				disabled: shouldDisableDocumentRow(processDocument)
			};
		},
		[data]
	);

	const CustomOverviewButtonComponent: ComponentType<OverviewButton.Props> = useMemo(
		() => CustomOverviewButton(user, fachverfahrenManifest),
		[user, fachverfahrenManifest]
	);

	const cachedComponentMap: ComponentMap = useMemo(() => {
		return {
			...DefaultComponentMap,
			FilterSelector: CustomFilterSelector,
			FilterOptionsViews: {
				...DefaultComponentMap.FilterOptionsViews,
				StringFilterOptionsView: CustomStringFilterOptionsView,
				EnumerationFilterOptionsView: CustomEnumerationFilterOptionsView
			},
			FilterBar: CustomFilterBar,
			FilterButton: CustomFilterButton,
			ReferenceCell: CustomFormatReferenceCell,
			TableBodyCell: CustomTableBodyCell,
			OverviewButton: ScopeBasedVisibilityDecorator(
				CustomOverviewButtonComponent,
				props => props.buttonModel.annotations,
				fachverfahrenManifest?.identification.key
			),
			ContextMenu: fachverfahrenManifest
				? CustomContextMenu(dispatch, user, activityId, documents, fachverfahrenManifest)
				: DefaultComponentMap.ContextMenu,
			RightClickContextMenu: fachverfahrenManifest
				? CustomContextMenu(dispatch, user, activityId, documents, fachverfahrenManifest)
				: DefaultComponentMap.RightClickContextMenu,
			RowAction: ScopeBasedVisibilityDecorator(
				DefaultComponentMap.RowAction,
				p => p.rowActionModel.annotations,
				fachverfahrenManifest?.identification.key
			),
			TableBody: StyledTableBody,
			Footer: createCustomFooterComponent(modelDescriptors)
		};
	}, [dispatch, user, activityId, documents, fachverfahrenManifest, CustomOverviewButtonComponent, modelDescriptors]);

	const cachedWidgetMap = useMemo(() => {
		return {
			...widgetsMap,
			PopUpMenu: CustomPopUpMenu(user, fachverfahrenManifest)
		};
	}, [user, fachverfahrenManifest]);

	const [showModal, setShowModal] = useState(false);

	const clients = useMemo(() => {
		const userClients = hasOidcUser
			? selectStructuredRoles(user)
					.filter(
						it =>
							it.key === fachverfahrenManifest?.identification.key &&
							it.roles.length > 0 &&
							(it.roles.length > 1 || it.roles[0] !== "betrachter")
					)
					.map(it => it.client ?? "")
			: [];

		return (
			fachverfahrenManifest?.clients
				?.map(it => (it.key === "​" ? "" : (it.key ?? "")))
				.filter(it => (hasOidcUser && isAdmin(user)) || userClients.includes(it)) ??
			Array.from(userClients.values())
		);
	}, [fachverfahrenManifest?.clients, fachverfahrenManifest?.identification.key, user, hasOidcUser]);

	const eventHandlers = useMemo(
		() => ({
			onEventButtonClick: (event: string, button?: OverviewModel.Button) => {
				if (event === "neuerAntrag") {
					if (clients.length <= 1 && fachverfahrenManifest) {
						createNewAntrag(dispatch, clients[0], fachverfahrenManifest, activityId);
					} else {
						setShowModal(true);
					}
				} else if (event === "export" && fachverfahrenManifest) {
					dispatch(exportApplicationData({ manifest: fachverfahrenManifest, anonym: false }));
				} else if (event === "exportAnonym" && fachverfahrenManifest) {
					dispatch(exportApplicationData({ manifest: fachverfahrenManifest, anonym: true }));
				} else if (event === "open_invitations" && fachverfahrenManifest) {
					const sourceAppKeys =
						button?.annotations?.find(({ name }) => name === EMAIL_KONTAKLISTE_APPIDS)?.value ?? "";
					const descriptorAppKeys = extractConfigList(sourceAppKeys).join(",");
					dispatch(
						openInvitationModal({
							applicationKey: fachverfahrenManifest.identification.key,
							sourceAppKeys: descriptorAppKeys,
							initiatingActivityId: activityId
						})
					);
				}
			},
			onRowClick(params: { documentId: string; customEvent?: string }) {
				const document = documents.find(doc => doc.id === params.documentId);
				if (!document) {
					logger.error("Unexpected Error - No Document found for id " + params.documentId);
					return;
				}
				dispatch(
					openTaskDocument({
						initiatingActivityId: activityId,
						document,
						instance: document.id
					})
				);
			}
		}),
		[fachverfahrenManifest, clients, dispatch, activityId, documents]
	);

	const onCloseModal = useCallback(() => {
		setShowModal(false);
	}, [setShowModal]);
	const onSubmitModal = useCallback<ClientModalProps["onSubmit"]>(
		client => {
			if (fachverfahrenManifest) {
				createNewAntrag(dispatch, client, fachverfahrenManifest, activityId);
				setShowModal(false);
			}
		},
		[activityId, dispatch, fachverfahrenManifest, setShowModal]
	);

	return (
		<>
			<ActivityIdContext.Provider value={activityId}>
				<DocumentModelContextProvider activityId={activityId}>
					<ExternalEnumOverviewEngine
						ariaLevel={props.ariaLevel}
						key="task-overview"
						name={name}
						modelDescriptors={modelDescriptors}
						activityId={activityId}
						eventHandlers={eventHandlers}
						componentMap={cachedComponentMap}
						widgetMap={cachedWidgetMap}
						rowStyling={rowStyling}
					/>
				</DocumentModelContextProvider>
			</ActivityIdContext.Provider>
			{showModal && <ClientModal clients={clients} onClose={onCloseModal} onSubmit={onSubmitModal} />}
		</>
	);
}

const CustomContentBox: ComponentType<ContentBoxProps> = function CustomContentBox(props) {
	const activityId = useContext(ActivityIdContext);
	return <DefaultWidgetMap.ContentBox {...props} id={getViewHtmlId(activityId)} />;
};

const widgetsMap: WidgetMap = {
	...GeneralOverviewWidgetsMap,
	ContentBox: CustomContentBox,
	Filter: CustomFilter,
	FilterSelectorMobile: CustomFilterSelectorMobile,
	FilterSelector: CustomFilterSelectorWidget
};

const ASSIGNEE_COLUMN_ID = "column-assignee-id";
const FRIST_ERFUELLT_COLUMN_ID = "column-frist-erfuellt";
const LINKS_COLUMN_ID = "column-linked-processes";
const WIEDERVORLAGE_FRUEHSTE_FAELIGKEITSDATUM_ELEMENT_REF_SUFFIX = "field_740dd";

const CustomTableBodyCell: ComponentType<TableBodyCell.Props> = function CustomTableBodyCell(props) {
	const dispatch = useDispatch();

	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const fachverfahren = useSelector(
		ActivitySelectors.activityPropById(activityId, it => it.descriptor.fachverfahren)
	);
	const manifest = useSelector(getFVManifest(fachverfahren));
	const fvKey = manifest?.identification.key;

	const hasEditPermission = useSelector(selectHasNeededPermissions(hasUpdateDocumentPermission, fvKey));

	const docRef = props.row.id;
	const candidates = useSelector(selectAssigneeCandidatesCreator(fvKey ?? "", "Vorgangszuweisung"));
	const assigneeRef = useRef<HTMLElement | undefined>(undefined);
	const setAssigneeRef = useCallback((element: HTMLElement | null) => {
		assigneeRef.current = element ?? undefined;
	}, []);
	const onChange = useCallback<(assignee: AssigneeCandidate | undefined) => void>(
		assignee =>
			dispatch(
				assignProcessDocument({
					activityId,
					docRef,
					assignee: assignee?.username ?? "",
					assigneeHtmlRef: assigneeRef.current
				})
			),
		[dispatch, activityId, docRef]
	);

	if (!isDocumentWithProcessMetadata(props.row)) {
		return <DefaultComponentMap.TableBodyCell {...props} />;
	}

	const { row, columnModel } = props;

	switch (columnModel.id) {
		case FRIST_ERFUELLT_COLUMN_ID:
			return props.row.root?.processMetadata?.fristen?.allFristenErfuellt ? (
				<Icon title="Alle Fristen erfüllt" showTitleAsTooltip iconTheme="outlined">
					event_available
				</Icon>
			) : (
				<></>
			);
		case ASSIGNEE_COLUMN_ID:
			if (
				fvKey &&
				hasEditPermission &&
				ReferenceColumn.isAssignableFrom(columnModel) &&
				!row.root?.processMetadata.isFinished &&
				!row.root?.processMetadata.archival?.archivalInProgress
			) {
				const fqn = row.root?.processMetadata.fachverfahren.fqn;
				return (
					<div style={fullWidthStyle} onClick={preventEvent}>
						<OverviewAssigneePicker
							id={`${activityId}-${row.id}-${columnModel.id}`}
							localizedModelText={columnModel.label}
							activityId={activityId}
							elementRef={columnModel.elementRef}
							document={row}
							fvKey={fvKey}
							fqn={fqn}
							candidates={candidates}
							onChange={onChange}
							inputRef={setAssigneeRef}
						/>
					</div>
				);
			}
			return <DefaultComponentMap.TableBodyCell {...props} />;
		case LINKS_COLUMN_ID:
			return <CustomLinksCell {...props}></CustomLinksCell>;
		default:
			if (
				ReferenceColumn.isAssignableFrom(columnModel) &&
				columnModel.elementRef.endsWith(WIEDERVORLAGE_FRUEHSTE_FAELIGKEITSDATUM_ELEMENT_REF_SUFFIX)
			) {
				return <CustomWiedervorlageMitFruehestenFaeligkeitsdatumCell {...props} />;
			}
			return <DefaultComponentMap.TableBodyCell {...props} />;
	}
};

function shouldDisableDocumentRow(document?: Activity.Data.Document): boolean {
	if (!isDocumentWithProcessMetadata(document)) {
		return true;
	}

	return (
		!isDocumentWithProcessMetadata(document) ||
		(!document.root.processMetadata.isFinished && !document.root.processMetadata.isInUserTask) // Wenn Antrag gerade abgeschlossen wird
	);
}

const fullWidthStyle = { width: "100%" };

function CustomLinksCell(props: TableBodyCell.Props): ReactElement {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	if (ExpressionColumn.isAssignableFrom(props.columnModel)) {
		throw Error("Not implemented."); // Wir arbeiten bisher nur mit ReferenceColumn
	}

	const dispatch = useDispatch();

	const [linkedProcesses, setLinkedProcesses] = useState<LinkedProcessInfo[]>([]);
	useEffect(() => {
		if (isDocumentWithProcessMetadata(props.row)) {
			setLinkedProcesses(props.row.root.processMetadata.links?.linkedProcesses ?? []);
		}
	}, [props.row]);

	const openLinkedProcess = (docRef: string): void => {
		dispatch(openTaskDocument({ instance: docRef }));
	};

	return (
		<div style={fullWidthStyle} onClick={preventEvent}>
			<ul>
				{linkedProcesses.map(it => (
					<li key={it.docRef}>
						<Link onClick={() => openLinkedProcess(it.docRef)}>
							{it.vorgangsnummer ?? "Vorgang"} ({it.fvShortName})
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}

function CustomWiedervorlageMitFruehestenFaeligkeitsdatumCell(props: TableBodyCell.Props): ReactElement {
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const dispatch = useDispatch();

	if (!isDocumentWithProcessMetadata(props.row)) {
		return <DefaultComponentMap.TableBodyCell {...props} />;
	}

	const row = props.row as ProcessMetadataContainingDocument & {
		root: {
			genehmigungsverfahren?: {
				wiedervorlagenMitFruehestenFaeligkeitsdatum?: { fruehestesFaeligkeitsdatum?: string };
			};
		};
	};
	const dateValue =
		row.root?.genehmigungsverfahren?.wiedervorlagenMitFruehestenFaeligkeitsdatum?.fruehestesFaeligkeitsdatum;
	if (!dateValue) {
		return <></>;
	}

	const formattedDate = new Date(dateValue).toLocaleDateString("de-DE", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric"
	});

	const handleClick = (): void => {
		dispatch(
			openTaskDocumentAndShowWiedervorlagen({
				initiatingActivityId: activityId,
				document: props.row as ProcessMetadataContainingDocument,
				instance: props.row.id
			})
		);
	};

	return (
		<div style={fullWidthStyle} onClick={preventEvent}>
			<Link onClick={handleClick}>{formattedDate}</Link>
		</div>
	);
}

function preventEvent(event: React.MouseEvent): void {
	event.preventDefault();
	event.stopPropagation(); // Prevent event from bubbling up to the overview row
}

export function createCustomFooterComponent(
	modelDescriptors?: Model.Descriptor[]
): (props: Footer.PropsType) => ReactElement {
	return function CustomFooter(props: Footer.PropsType): ReactElement {
		const activityId = useContext(ActivityIdContext);
		const data = useSelector(ActivitySelectors.data(activityId ?? ""));
		const totalCount = data && isProcessOverviewData(data) ? data.totalDocumentsCount : undefined;
		const hideFooter = modelDescriptors?.some(e => MODELS_WITHOUT_PAGINATION.includes(e.name));

		if (hideFooter) {
			// "Letzte Änderungen": no footer at all
			return <></>;
		}
		return <OverviewFooterWithCount {...props} totalCount={totalCount} />;
	};
}
